import type { BlockState } from "./blockSpec.ts";
import type { BlockEntityData } from "./blockEntityData.ts";
import {
  cloneBlockEntityData
} from "./blockEntityData.ts";
import {
  refreshConnectionsAround
} from "./blockPlacement.ts";
import type { VoxelWorld } from "./VoxelWorld.ts";

export interface GridSelectionPosition {
  x: number;
  y: number;
  z: number;
}

export interface SelectionBounds {
  min: GridSelectionPosition;
  max: GridSelectionPosition;
  size: GridSelectionPosition;
  volume: number;
}

export interface SelectionClipboardVoxel {
  x: number;
  y: number;
  z: number;
  blockId: string;
  state: BlockState;
  blockEntity: BlockEntityData | null;
}

export interface SelectionClipboard {
  voxels: SelectionClipboardVoxel[];
  size: GridSelectionPosition;
  sourceVersion: string | null;
}

const CARDINAL_DIRECTIONS = [
  "north",
  "east",
  "south",
  "west"
] as const;

function rotateDirection(
  direction: string,
  quarterTurns: number
): string {
  const index = CARDINAL_DIRECTIONS.indexOf(
    direction as (typeof CARDINAL_DIRECTIONS)[number]
  );
  if (index < 0) return direction;
  const normalizedTurns =
    ((quarterTurns % 4) + 4) % 4;
  return CARDINAL_DIRECTIONS[
    (index + normalizedTurns) % 4
  ];
}

function mirrorDirectionX(direction: string): string {
  if (direction === "east") return "west";
  if (direction === "west") return "east";
  return direction;
}

function mirrorDirectionZ(direction: string): string {
  if (direction === "north") return "south";
  if (direction === "south") return "north";
  return direction;
}

function canonicalRailPair(
  first: string,
  second: string
): string | null {
  const pair = new Set([first, second]);
  if (pair.has("north") && pair.has("south")) {
    return "north_south";
  }
  if (pair.has("east") && pair.has("west")) {
    return "east_west";
  }
  if (pair.has("north") && pair.has("east")) {
    return "north_east";
  }
  if (pair.has("north") && pair.has("west")) {
    return "north_west";
  }
  if (pair.has("south") && pair.has("east")) {
    return "south_east";
  }
  if (pair.has("south") && pair.has("west")) {
    return "south_west";
  }
  return null;
}

function transformDirectionalShape(
  shape: string,
  transformDirection: (direction: string) => string
): string {
  if (shape.startsWith("ascending_")) {
    return "ascending_" +
      transformDirection(shape.slice("ascending_".length));
  }

  const pair = shape.split("_");
  if (
    pair.length === 2 &&
    CARDINAL_DIRECTIONS.includes(
      pair[0] as (typeof CARDINAL_DIRECTIONS)[number]
    ) &&
    CARDINAL_DIRECTIONS.includes(
      pair[1] as (typeof CARDINAL_DIRECTIONS)[number]
    )
  ) {
    return (
      canonicalRailPair(
        transformDirection(pair[0]),
        transformDirection(pair[1])
      ) || shape
    );
  }

  return shape;
}

function rotateStandingRotation(
  value: string,
  quarterTurns: number
): string {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 15) {
    return value;
  }
  const normalizedTurns =
    ((quarterTurns % 4) + 4) % 4;
  return String((parsed + normalizedTurns * 4) % 16);
}

function mirrorStandingRotationX(value: string): string {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 15) {
    return value;
  }
  return String((16 - parsed) % 16);
}

function mirrorStandingRotationZ(value: string): string {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 15) {
    return value;
  }
  return String((8 - parsed + 16) % 16);
}

function rotateCardinalProperties(
  state: BlockState,
  quarterTurns: number
): BlockState {
  const next = { ...state };
  const values: Record<string, string | undefined> = {};

  CARDINAL_DIRECTIONS.forEach((direction) => {
    values[direction] = state[direction];
    delete next[direction];
  });

  CARDINAL_DIRECTIONS.forEach((direction) => {
    const value = values[direction];
    if (value === undefined) return;
    next[rotateDirection(direction, quarterTurns)] = value;
  });

  return next;
}

export function rotateBlockStateY(
  inputState: BlockState,
  degrees: number
): BlockState {
  const normalizedDegrees =
    ((Math.round(degrees / 90) * 90) % 360 + 360) % 360;
  const quarterTurns = normalizedDegrees / 90;
  let state = { ...(inputState || {}) };

  if (quarterTurns === 0) return state;

  if (state.facing) {
    state.facing = rotateDirection(state.facing, quarterTurns);
  }
  if (
    state.axis &&
    quarterTurns % 2 === 1
  ) {
    if (state.axis === "x") state.axis = "z";
    else if (state.axis === "z") state.axis = "x";
  }
  if (state.rotation !== undefined) {
    state.rotation = rotateStandingRotation(
      state.rotation,
      quarterTurns
    );
  }
  if (state.shape) {
    state.shape = transformDirectionalShape(
      state.shape,
      (direction) =>
        rotateDirection(direction, quarterTurns)
    );
  }

  const hasCardinalProperties = CARDINAL_DIRECTIONS.some(
    (direction) => state[direction] !== undefined
  );
  if (hasCardinalProperties) {
    state = rotateCardinalProperties(state, quarterTurns);
  }

  return state;
}

function mirrorCardinalProperties(
  inputState: BlockState,
  axis: "x" | "z"
): BlockState {
  const state = { ...inputState };
  if (axis === "x") {
    const east = state.east;
    if (east !== undefined || state.west !== undefined) {
      state.east = state.west;
      state.west = east;
    }
  } else {
    const north = state.north;
    if (north !== undefined || state.south !== undefined) {
      state.north = state.south;
      state.south = north;
    }
  }
  return state;
}

function toggleHandedState(inputState: BlockState): BlockState {
  const state = { ...inputState };

  if (state.hinge === "left") state.hinge = "right";
  else if (state.hinge === "right") state.hinge = "left";

  if (state.shape === "inner_left") {
    state.shape = "inner_right";
  } else if (state.shape === "inner_right") {
    state.shape = "inner_left";
  } else if (state.shape === "outer_left") {
    state.shape = "outer_right";
  } else if (state.shape === "outer_right") {
    state.shape = "outer_left";
  }

  return state;
}

export function mirrorBlockStateX(
  inputState: BlockState
): BlockState {
  let state = { ...(inputState || {}) };

  if (state.facing) {
    state.facing = mirrorDirectionX(state.facing);
  }
  if (state.rotation !== undefined) {
    state.rotation = mirrorStandingRotationX(state.rotation);
  }
  if (state.shape) {
    state.shape = transformDirectionalShape(
      state.shape,
      mirrorDirectionX
    );
  }
  state = mirrorCardinalProperties(state, "x");
  state = toggleHandedState(state);
  return state;
}

export function mirrorBlockStateZ(
  inputState: BlockState
): BlockState {
  let state = { ...(inputState || {}) };

  if (state.facing) {
    state.facing = mirrorDirectionZ(state.facing);
  }
  if (state.rotation !== undefined) {
    state.rotation = mirrorStandingRotationZ(state.rotation);
  }
  if (state.shape) {
    state.shape = transformDirectionalShape(
      state.shape,
      mirrorDirectionZ
    );
  }
  state = mirrorCardinalProperties(state, "z");
  state = toggleHandedState(state);
  return state;
}

export function getSelectionBounds(
  start: GridSelectionPosition,
  end: GridSelectionPosition = start
): SelectionBounds {
  const min = {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    z: Math.min(start.z, end.z)
  };
  const max = {
    x: Math.max(start.x, end.x),
    y: Math.max(start.y, end.y),
    z: Math.max(start.z, end.z)
  };
  const size = {
    x: max.x - min.x + 1,
    y: max.y - min.y + 1,
    z: max.z - min.z + 1
  };

  return {
    min,
    max,
    size,
    volume: size.x * size.y * size.z
  };
}

export function captureSelection(
  world: VoxelWorld,
  start: GridSelectionPosition,
  end: GridSelectionPosition = start
): SelectionClipboard {
  const bounds = getSelectionBounds(start, end);
  const voxels: SelectionClipboardVoxel[] = [];

  for (let y = bounds.min.y; y <= bounds.max.y; y += 1) {
    for (let z = bounds.min.z; z <= bounds.max.z; z += 1) {
      for (let x = bounds.min.x; x <= bounds.max.x; x += 1) {
        const block = world.getBlockData(x, y, z);
        if (!block) continue;

        voxels.push({
          x: x - bounds.min.x,
          y: y - bounds.min.y,
          z: z - bounds.min.z,
          blockId: block.blockId,
          state: { ...block.state },
          blockEntity: cloneBlockEntityData(
            world.getBlockEntityData(x, y, z)
          )
        });
      }
    }
  }

  return {
    voxels,
    size: { ...bounds.size },
    sourceVersion: world.minecraftVersion
  };
}

export function deleteSelection(
  world: VoxelWorld,
  start: GridSelectionPosition,
  end: GridSelectionPosition = start
): number {
  const bounds = getSelectionBounds(start, end);
  const changedPositions: GridSelectionPosition[] = [];
  let changed = 0;

  world.runBatch(() => {
    for (let y = bounds.min.y; y <= bounds.max.y; y += 1) {
      for (let z = bounds.min.z; z <= bounds.max.z; z += 1) {
        for (let x = bounds.min.x; x <= bounds.max.x; x += 1) {
          if (world.removeBlock(x, y, z)) {
            changed += 1;
            changedPositions.push({ x, y, z });
          }
        }
      }
    }
    refreshConnectionsAround(world, changedPositions);
  });

  return changed;
}

function rotateClipboardOnce(
  clipboard: SelectionClipboard
): SelectionClipboard {
  const nextSize = {
    x: clipboard.size.z,
    y: clipboard.size.y,
    z: clipboard.size.x
  };

  return {
    sourceVersion: clipboard.sourceVersion,
    size: nextSize,
    voxels: clipboard.voxels.map((voxel) => ({
      ...voxel,
      x: clipboard.size.z - 1 - voxel.z,
      z: voxel.x,
      state: rotateBlockStateY(voxel.state, 90),
      blockEntity: cloneBlockEntityData(voxel.blockEntity)
    }))
  };
}

export function rotateSelectionClipboardY(
  clipboard: SelectionClipboard,
  degrees = 90
): SelectionClipboard {
  const normalizedDegrees =
    ((Math.round(degrees / 90) * 90) % 360 + 360) % 360;
  let result: SelectionClipboard = {
    sourceVersion: clipboard.sourceVersion,
    size: { ...clipboard.size },
    voxels: clipboard.voxels.map((voxel) => ({
      ...voxel,
      state: { ...voxel.state },
      blockEntity: cloneBlockEntityData(voxel.blockEntity)
    }))
  };

  for (
    let turn = 0;
    turn < normalizedDegrees / 90;
    turn += 1
  ) {
    result = rotateClipboardOnce(result);
  }

  return result;
}

export function mirrorSelectionClipboardX(
  clipboard: SelectionClipboard
): SelectionClipboard {
  return {
    sourceVersion: clipboard.sourceVersion,
    size: { ...clipboard.size },
    voxels: clipboard.voxels.map((voxel) => ({
      ...voxel,
      x: clipboard.size.x - 1 - voxel.x,
      state: mirrorBlockStateX(voxel.state),
      blockEntity: cloneBlockEntityData(voxel.blockEntity)
    }))
  };
}

export function mirrorSelectionClipboardZ(
  clipboard: SelectionClipboard
): SelectionClipboard {
  return {
    sourceVersion: clipboard.sourceVersion,
    size: { ...clipboard.size },
    voxels: clipboard.voxels.map((voxel) => ({
      ...voxel,
      z: clipboard.size.z - 1 - voxel.z,
      state: mirrorBlockStateZ(voxel.state),
      blockEntity: cloneBlockEntityData(voxel.blockEntity)
    }))
  };
}

export function pasteSelection(
  world: VoxelWorld,
  clipboard: SelectionClipboard,
  origin: GridSelectionPosition
): number {
  const changedPositions = new Set<string>();
  const touchedPositions: GridSelectionPosition[] = [];

  world.runBatch(() => {
    clipboard.voxels.forEach((voxel) => {
      const position = {
        x: origin.x + voxel.x,
        y: origin.y + voxel.y,
        z: origin.z + voxel.z
      };
      let changed = world.setBlock(
        position.x,
        position.y,
        position.z,
        voxel.blockId,
        voxel.state
      );

      if (
        world.setBlockEntityData(
          position.x,
          position.y,
          position.z,
          voxel.blockEntity
        )
      ) {
        changed = true;
      }

      if (changed) {
        changedPositions.add(
          position.x + "," + position.y + "," + position.z
        );
      }
      touchedPositions.push(position);
    });

    refreshConnectionsAround(world, touchedPositions);
  });

  return changedPositions.size;
}


export function pasteSelectionArray(
  world: VoxelWorld,
  clipboard: SelectionClipboard,
  sourceOrigin: GridSelectionPosition,
  offset: GridSelectionPosition,
  additionalCopies: number
): number {
  const copyCount = Math.max(
    0,
    Math.min(64, Math.round(additionalCopies))
  );
  let changed = 0;

  world.runBatch(() => {
    for (let index = 1; index <= copyCount; index += 1) {
      changed += pasteSelection(
        world,
        clipboard,
        {
          x: sourceOrigin.x + offset.x * index,
          y: sourceOrigin.y + offset.y * index,
          z: sourceOrigin.z + offset.z * index
        }
      );
    }
  });

  return changed;
}
